import { createStore } from 'vuex'
import authService from '../services/authService'
import departmentService from '../services/departmentService'

// Helper function to parse permissions into modules and actions
function parsePermissions(permissions) {
  const modules = {}
  
  permissions.forEach(permission => {
    // Parse permission format: admin.module_name.action_name
    const parts = permission.split('.')
    if (parts.length === 3 && parts[0] === 'admin') {
      const [, moduleName, actionName] = parts
      
      if (!modules[moduleName]) {
        modules[moduleName] = {
          name: moduleName,
          actions: []
        }
      }
      
      if (!modules[moduleName].actions.includes(actionName)) {
        modules[moduleName].actions.push(actionName)
      }
    }
  })
  
  return modules
}

export default createStore({
  state: {
    user: JSON.parse(localStorage.getItem('user')) || null,
    token: localStorage.getItem('token') || null,
    departments: JSON.parse(localStorage.getItem('departments')) || [],
    selectedDepartment: JSON.parse(localStorage.getItem('selectedDepartment')) || null,
    roles: JSON.parse(localStorage.getItem('roles')) || [],
    permissions: JSON.parse(localStorage.getItem('permissions')) || [],
    modules: JSON.parse(localStorage.getItem('modules')) || {},
    sendPermissions: JSON.parse(localStorage.getItem('sendPermissions')) || [],
    isAuthenticated: !!localStorage.getItem('token'),
    isLoggingOut: false,
    folders: [
      { id: 1, name: 'مهم', color: '#ff5722', emailCount: 3 },
      { id: 2, name: 'متابعة', color: '#2196f3', emailCount: 7 },
      { id: 3, name: 'شخصي', color: '#4caf50', emailCount: 2 }
    ]
  },
  
  mutations: {
    SET_USER(state, user) {
      state.user = user
      if (user) {
        localStorage.setItem('user', JSON.stringify(user))
      } else {
        localStorage.removeItem('user')
      }
    },
    
    SET_TOKEN(state, token) {
      state.token = token
      state.isAuthenticated = !!token
      if (token) {
        localStorage.setItem('token', token)
      } else {
        localStorage.removeItem('token')
      }
    },
    
    SET_ROLES(state, roles) {
      state.roles = roles
      if (roles && roles.length > 0) {
        localStorage.setItem('roles', JSON.stringify(roles))
      } else {
        localStorage.removeItem('roles')
      }
    },
    
    SET_PERMISSIONS(state, permissions) {
      state.permissions = permissions
      if (permissions && permissions.length > 0) {
        localStorage.setItem('permissions', JSON.stringify(permissions))
      } else {
        localStorage.removeItem('permissions')
      }
    },
    
    SET_MODULES(state, modules) {
      state.modules = modules
      if (modules) {
        localStorage.setItem('modules', JSON.stringify(modules))
      } else {
        localStorage.removeItem('modules')
      }
    },
    
    SET_SEND_PERMISSIONS(state, sendPermissions) {
      state.sendPermissions = sendPermissions
      if (sendPermissions && sendPermissions.length > 0) {
        localStorage.setItem('sendPermissions', JSON.stringify(sendPermissions))
      } else {
        localStorage.removeItem('sendPermissions')
      }
    },
    
    SET_DEPARTMENTS(state, departments) {
      state.departments = departments
      if (departments && departments.length > 0) {
        localStorage.setItem('departments', JSON.stringify(departments))
      } else {
        localStorage.removeItem('departments')
      }
    },
    
    SET_SELECTED_DEPARTMENT(state, department) {
      state.selectedDepartment = department
      if (department) {
        localStorage.setItem('selectedDepartment', JSON.stringify(department))
      } else {
        localStorage.removeItem('selectedDepartment')
      }
    },
    
    SET_LOGGING_OUT(state, status) {
      state.isLoggingOut = status
    },

    CLEAR_AUTH(state) {
      state.user = null
      state.token = null
      state.departments = []
      state.selectedDepartment = null
      state.roles = []
      state.permissions = []
      state.modules = {}
      state.sendPermissions = []
      state.isAuthenticated = false
      state.isLoggingOut = false
      localStorage.removeItem('user')
      localStorage.removeItem('token')
      localStorage.removeItem('departments')
      localStorage.removeItem('selectedDepartment')
      localStorage.removeItem('roles')
      localStorage.removeItem('permissions')
      localStorage.removeItem('modules')
      localStorage.removeItem('sendPermissions')
    },
    
    ADD_FOLDER(state, folder) {
      state.folders.push(folder)
    },
    
    UPDATE_FOLDER(state, updatedFolder) {
      const index = state.folders.findIndex(folder => folder.id === updatedFolder.id)
      if (index !== -1) {
        state.folders.splice(index, 1, updatedFolder)
      }
    },
    
    DELETE_FOLDER(state, folderId) {
      state.folders = state.folders.filter(folder => folder.id !== folderId)
    }
  },
  
  actions: {
    async login({ commit }, credentials) {
      try {
        const response = await authService.login(credentials)
        
        if (response.success) {
          const { user, token } = response.data
          
          // Store user data and token
          commit('SET_USER', user)
          commit('SET_TOKEN', token)
          
                    // Handle departments - fetch department details and merge with user departments
          const userDepartments = user.departments || []
          
          // Validate that user has at least one department
          if (userDepartments.length === 0) {
            throw new Error('المستخدم يجب أن يكون مُسجل في قسم واحد على الأقل')
          }
          
          try {
            // Fetch all departments to get department names
            const departmentsResponse = await departmentService.getDepartments()
            const allDepartments = departmentsResponse.data?.data || departmentsResponse.data || []
            
            // Merge user departments with department details, preserving send permissions from login response
            const enrichedDepartments = userDepartments.map(userDept => {
              const deptInfo = allDepartments.find(dept => dept.id === userDept.department_id)
              return {
                ...userDept,
                department: {
                  // Use department info from API (for names, stamps, etc.)
                  ...(deptInfo || { id: userDept.department_id, name: `القسم ${userDept.department_id}` }),
                  // Preserve user-specific send permissions from login response
                  send_permissions_ids: userDept.department?.send_permissions_ids || [],
                  send_permissions: userDept.department?.send_permissions || []
                }
              }
            })
            
            commit('SET_DEPARTMENTS', enrichedDepartments)
            
            // Set first department as default with its roles, permissions, and send permissions
            const firstDepartment = enrichedDepartments[0]
            commit('SET_SELECTED_DEPARTMENT', firstDepartment)
            commit('SET_ROLES', firstDepartment.roles || [])
            
            // Process permissions into modules and actions
            const permissions = firstDepartment.permissions || []
            commit('SET_PERMISSIONS', permissions)
            
            // Parse permissions into modules
            const modules = parsePermissions(permissions)
            commit('SET_MODULES', modules)
            
            // Extract send permissions from the nested department object
            const sendPermissions = firstDepartment.department?.send_permissions_ids || []
            commit('SET_SEND_PERMISSIONS', sendPermissions)
            
            //console.log('✅ Login successful - Department system initialized:')
            //console.log('👥 Total departments:', enrichedDepartments.length)
            //console.log('🏢 Selected department:', firstDepartment.department?.name || firstDepartment.department_id)
            //console.log('🎭 Roles:', firstDepartment.roles?.length || 0)
            //console.log('🔑 Permissions:', firstDepartment.permissions?.length || 0)
            //console.log('📦 Modules:', Object.keys(modules).length, modules)
            //console.log('📤 Send permissions (from department.send_permissions_ids):', sendPermissions.length || 0, sendPermissions)
            //console.log('🔍 Department object:', firstDepartment.department)
            
                      } catch (error) {
              commit('SET_DEPARTMENTS', userDepartments)
              const firstDepartment = userDepartments[0]
              commit('SET_SELECTED_DEPARTMENT', firstDepartment)
              commit('SET_ROLES', firstDepartment.roles || [])
              
              // Process permissions into modules and actions
              const permissions = firstDepartment.permissions || []
              commit('SET_PERMISSIONS', permissions)
              
              // Parse permissions into modules
              const modules = parsePermissions(permissions)
              commit('SET_MODULES', modules)
              
              // Extract send permissions from the nested department object
              const sendPermissions = firstDepartment.department?.send_permissions_ids || []
              commit('SET_SEND_PERMISSIONS', sendPermissions)
              
              //console.log('⚠️ Login successful - Using basic department info:')
              //console.log('👥 Total departments:', userDepartments.length)
              //console.log('🏢 Selected department ID:', firstDepartment.department_id)
              //console.log('🎭 Roles:', firstDepartment.roles?.length || 0)
              //console.log('🔑 Permissions:', firstDepartment.permissions?.length || 0)
              //console.log('📦 Modules:', Object.keys(modules).length, modules)
              //console.log('📤 Send permissions (from department.send_permissions_ids):', sendPermissions.length || 0, sendPermissions)
              //console.log('🔍 Department object:', firstDepartment.department)
            }
          
          return response
        } else {
          throw new Error(response.message || 'فشل في تسجيل الدخول')
        }
      } catch (error) {
        commit('CLEAR_AUTH')
        
        // Provide Arabic error messages
        if (error.response) {
          const status = error.response.status
          const data = error.response.data
          
          if (status === 401) {
            error.message = 'اسم المستخدم أو كلمة المرور غير صحيحة'
          } else if (status === 422) {
            error.message = data.message || 'البيانات المدخلة غير صحيحة'
          } else if (status === 429) {
            error.message = 'تم تجاوز عدد المحاولات المسموحة، يرجى المحاولة لاحقاً'
          } else if (status >= 500) {
            error.message = 'خطأ في الخادم، يرجى المحاولة لاحقاً'
          } else {
            error.message = data.message || 'حدث خطأ غير متوقع'
          }
        } else if (error.code === 'ECONNABORTED') {
          error.message = 'انتهت مهلة الاتصال، يرجى المحاولة مرة أخرى'
        } else if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
          error.message = 'تعذر الاتصال بالخادم، يرجى التحقق من الاتصال بالإنترنت'
        } else if (!error.message) {
          error.message = 'فشل في تسجيل الدخول'
        }
        
        throw error
      }
    },
    
    async logout({ commit, state }) {
      // Prevent multiple logout calls
      if (state.isLoggingOut) {
        return
      }

      commit('SET_LOGGING_OUT', true)
      
      // Small delay to allow UI updates to complete
      await new Promise(resolve => setTimeout(resolve, 100))
      
      try {
        await authService.logout()
      } catch (error) {
        console.error('Logout error:', error)
        // Don't throw error to prevent interrupting logout flow
      } finally {
        commit('CLEAR_AUTH')
        sessionStorage.clear()
      }
    },
    
    async refreshToken({ commit }) {
      try {
        const response = await authService.refreshToken()
        if (response.success && response.data.token) {
          commit('SET_TOKEN', response.data.token)
          return response.data.token
        }
      } catch (error) {
        commit('CLEAR_AUTH')
        throw error
      }
    },
    
    addFolder({ commit }, folder) {
      commit('ADD_FOLDER', folder)
    },
    
    updateFolder({ commit }, folder) {
      commit('UPDATE_FOLDER', folder)
    },
    
    deleteFolder({ commit }, folderId) {
      commit('DELETE_FOLDER', folderId)
    },
    
    switchDepartment({ commit, state }, departmentId) {
      const department = state.departments.find(dept => dept.department_id === departmentId)
      if (department) {
        commit('SET_SELECTED_DEPARTMENT', department)
        commit('SET_ROLES', department.roles || [])
        
        // Process permissions into modules and actions
        const permissions = department.permissions || []
        commit('SET_PERMISSIONS', permissions)
        
        // Parse permissions into modules
        const modules = parsePermissions(permissions)
        commit('SET_MODULES', modules)
        
        // Extract and set send permissions from the nested department object
        const sendPermissions = department.department?.send_permissions_ids || []
        commit('SET_SEND_PERMISSIONS', sendPermissions)
        
        //console.log('🔄 Department switched:')
        //console.log('🏢 New department:', department.department?.name || department.department_id)
        //console.log('🎭 New roles:', department.roles?.length || 0)
        //console.log('🔑 New permissions:', department.permissions?.length || 0)
        //console.log('📦 Modules:', Object.keys(modules).length, modules)
        //console.log('📤 Send permissions (from department.send_permissions_ids):', sendPermissions.length || 0, sendPermissions)
        //console.log('🔍 Department object:', department.department)
        
        return department
      } else {
        console.error('❌ Department not found:', departmentId)
        return null
      }
    }
  },
  
  getters: {
    isAuthenticated: state => state.isAuthenticated,
    isLoggingOut: state => state.isLoggingOut,
    user: state => state.user,
    departments: state => state.departments,
    selectedDepartment: state => state.selectedDepartment,
    currentDepartmentId: state => state.selectedDepartment?.department_id || null,
    currentDepartmentName: state => {
      if (state.selectedDepartment && state.selectedDepartment.department) {
        return state.selectedDepartment.department.name
      }
      return state.selectedDepartment?.department_name || ''
    },
    userRole: state => state.roles.length > 0 ? state.roles[0]?.name || state.roles[0] : '',
    hasPermission: state => permission => state.permissions.includes(permission),
    hasModule: state => moduleName => !!state.modules[moduleName],
    hasAction: state => (moduleName, actionName) => {
      const module = state.modules[moduleName]
      return module && module.actions.includes(actionName)
    },
    hasAnyAction: state => (moduleName, actions) => {
      const module = state.modules[moduleName]
      if (!module) return false
      return actions.some(action => module.actions.includes(action))
    },
    getModuleActions: state => moduleName => {
      const module = state.modules[moduleName]
      return module ? module.actions : []
    },
    availableModules: state => Object.keys(state.modules),
    modules: state => state.modules,
    hasRole: state => role => {
      if (typeof role === 'string') {
        return state.roles.some(r => (r.name || r) === role)
      }
      return state.roles.includes(role)
    },
    sendPermissions: state => state.sendPermissions,
    folders: state => state.folders
  },
  
  modules: {
    alerts: {
      namespaced: true,
      state: {
        alerts: []
      },
      
      mutations: {
        ADD_ALERT(state, alert) {
          state.alerts.push(alert)
        },
        
        REMOVE_ALERT(state, alertId) {
          state.alerts = state.alerts.filter(alert => alert.id !== alertId)
        },
        
        CLEAR_ALERTS(state) {
          state.alerts = []
        }
      },
      
      actions: {
        showAlert({ commit }, alert) {
          commit('ADD_ALERT', alert)
          
        
            setTimeout(() => {
              commit('REMOVE_ALERT', alert.id)
            }, 3000)
        
        },
        
        removeAlert({ commit }, alertId) {
          commit('REMOVE_ALERT', alertId)
        },
        
        clearAlerts({ commit }) {
          commit('CLEAR_ALERTS')
        }
      },
      
      getters: {
        alerts: state => state.alerts
      }
    }
  }
}) 